import type { AuthPermissionUser } from '../../services/authPermissions';

const INVOICE_KSEF_ACTION_ROLES = ['ADMIN', 'OPERATIONS'];

export function canDeleteInvoice(user: AuthPermissionUser | null | undefined): boolean {
  return user?.role === 'ADMIN';
}

export function canSendInvoiceToKSeF(user: AuthPermissionUser | null | undefined): boolean {
  return INVOICE_KSEF_ACTION_ROLES.includes(user?.role || '');
}

/** Minimal shape of the /api/invoices/ksef/status response the UI gates on. */
export interface KSeFModuleStatus {
  /**
   * True only when the KSeF module is on AND its integration is enabled. This is
   * the gate for "is the KSeF surface present at all". Absent on a stale backend
   * that predates the moduleEnabled/available split (see fallback below).
   */
  moduleEnabled?: boolean;
  /**
   * Narrowly means "the external KSeF system is reachable/ok". Stays false while
   * the module is off; can also be false while the module IS on but the KSeF
   * servers are temporarily unreachable (offline warning, statuses still shown).
   */
  available?: boolean;
}

/**
 * Single home for the "should any KSeF UI render at all" decision on the
 * invoices page.
 *
 * The KSeF status now carries two distinct signals: `moduleEnabled` ("is the
 * KSeF surface present at all") and `available` ("is the external KSeF system
 * reachable"). Surface visibility is gated on `moduleEnabled` so that when the
 * module is ON but the KSeF servers are temporarily unreachable
 * (moduleEnabled:true, available:false) operators still SEE their sent-invoice
 * statuses plus an offline warning, instead of the page blanking KSeF entirely.
 *
 * Backward-compat fallback: a stale backend that predates the split does not
 * send `moduleEnabled`. In that case we fall back to the old rule and treat
 * `available === true` as visible, so an out-of-date backend never blanks the
 * KSeF surface (nor shows it while the module is disabled, since old disabled
 * payloads report available:false).
 */
export function isKSeFSurfaceVisible(
  status: KSeFModuleStatus | null | undefined,
): boolean {
  if (status?.moduleEnabled !== undefined) {
    return status.moduleEnabled === true;
  }
  return status?.available === true;
}
